import { useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';

const FAQS = [
  {
    q: 'Is it really free?',
    a: `Free for normal use: five secure sends a month, with no per-transfer fee, no file-size paywall, and no credit card to start. Past five in a month, unlimited sending is $19.99 a year for an individual sender, and it is included at no extra cost with Practice Autopilot and AI Dental Office. We make money from the broader Intake.Dental platform (digital intake forms, AI clinical notes, insurance verification) — Send is built to introduce practices to the brand.`
  },
  {
    q: 'How is this different from email or Dropbox?',
    a: `Email isn't HIPAA-compliant for PHI without explicit BAA coverage and end-to-end encryption — most dental offices don't have that in place. Dropbox can be HIPAA-compliant but requires a paid Business plan, separate BAA paperwork, and a workflow patients and specialists never adopt. Send is single-purpose: a link in their inbox, a PIN you texted, a one-minute flow built around dentistry.`
  },
  {
    q: 'What file types can I send?',
    a: `Anything. DICOM, CBCT, intraoral scans (STL, PLY, OBJ), JPG, PNG, PDF, ZIP, MP4 — all supported. There is no whitelist. Up to 10 GB per file, multiple files per transfer. Scans, x-rays, photos and PDFs preview right in the browser; STL, PLY and OBJ open in 3D.`
  },
  {
    q: 'Can the recipient send files back?',
    a: `Yes. Every package has a "Send files back" button: a specialist can return a report or post-op scan straight to you, filed in the same case. Returns are free and never count toward anyone's monthly sends.`
  },
  {
    q: 'Do recipients need an account?',
    a: `No. They click the link in the email we sent them, enter the 4-digit PIN you texted them, and download. No password, no signup, no software install.`
  },
  {
    q: 'How long do files stay available?',
    a: `7 days from the moment you send. After that, the link stops working and the files are deleted from object storage. We don't offer indefinite retention by design — Send is a transfer tool, not a records archive.`
  },
  {
    q: 'Can I revoke a transfer after I send it?',
    a: `Yes. Open your dashboard, find the transfer, and revoke it. The link stops working immediately, even if the recipient hadn't opened it yet.`
  },
  {
    q: 'Is the BAA included?',
    a: `Yes. You accept our Business Associate Agreement on first sign-in (one click). It is on file, dated, and downloadable from your dashboard. No fax, no PDF, no procurement cycle.`
  },
  {
    q: 'Where are files stored?',
    a: `Encrypted object storage in a US AWS region. Files are AES-256 encrypted at rest. Database identifiers are field-level encrypted with rotating keys.`
  },
  {
    q: 'What happens if I lose the PIN?',
    a: `Open the transfer in your dashboard and reset the PIN. The recipient's link stays valid; they just need the new code.`
  },
  {
    q: 'Why do I have to text the PIN — can\'t the recipient just get it in the email?',
    a: `Splitting the access link from the unlock code is what keeps a single compromised inbox from being enough to access patient files. It is the most practical implementation of HIPAA's access-control rule — and it takes 5 seconds to text the PIN.`
  },
  {
    q: 'Can I send to multiple recipients at once?',
    a: `Yes. Add as many email addresses as you need. Each recipient gets the same link and the same PIN. Activity is logged per recipient.`
  },
  {
    q: 'Do you keep copies of my patient files?',
    a: `Files are retained only for the 7-day transfer window, then permanently deleted. We do not back them up to long-term storage. We do not analyze them. We do not feed them to any AI model.`
  },
  {
    q: 'Who is Intake.Dental?',
    a: `A dental software company founded by Dr. Jordan Thomas, a practicing dentist. Our flagship product is HIPAA-compliant digital patient intake forms; Send is a free standalone tool built on the same secure infrastructure.`
  },
  {
    q: 'Can I integrate Send with my PMS?',
    a: `If you're an Intake.Dental practice, yes. Send from your dashboard, search the patient by name, and the send is filed to that patient's chart under Sent imaging, with who received it and whether they opened it; your Sends list filters by patient too. Every practice plan can do this within its five free sends a month. Practice Autopilot and AI Dental Office include unlimited patient-linked sending. Otherwise, Send works as a standalone web app.`
  },
  {
    q: 'Where can I get help?',
    a: `Email support@intake.dental — typically same-day response from a real dental software person, not a chatbot.`
  },
];

// Exported for scripts/prerender.mjs — see the note in HowItWorks.jsx.
export const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

function Item({ q, a, open, onToggle }) {
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-q" onClick={onToggle} aria-expanded={open}>
        <span>{q}</span>
        <span className="faq-chev" aria-hidden>{open ? '−' : '+'}</span>
      </button>
      {open && <div className="faq-a"><p>{a}</p></div>}
    </div>
  );
}

export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="landing">
      <JsonLd id="faq" data={FAQ_SCHEMA} />
      <section className="hero">
        <div className="eyebrow">Frequently asked questions</div>
        <h1>Honest answers about Send.</h1>
        <p className="hero-lead">
          Built by a dentist for dentists, with as much technical detail as you want and
          none of the marketing fluff.
        </p>
      </section>

      <section className="content-section">
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <Item
              key={i}
              q={f.q}
              a={f.a}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </section>

      <section className="cta-section">
        <h2>Still have questions?</h2>
        <p className="muted">Email <a href="mailto:support@intake.dental">support@intake.dental</a> — same-day response from a real human.</p>
        <Link to="/" className="btn btn-primary btn-large">Send a file</Link>
      </section>
    </div>
  );
}
